import React from "react";
import type { Theme } from "../lib/theme";
import {
  type SubmittalStatus, type Submittal, type SubmittalTemplateCustomLayer,
  DIVISION_SUBMITTAL_TEMPLATES, SUBMITTAL_STATUS_CONFIG,
  proposeSubmittals, buildSubmittals, AddCustomTemplateItemForm,
  type SubmittalCandidate,
} from "../lib/submittals";
import { ALL_DIVISIONS } from "../projectData";
import * as Icon from "../components/OEIcons";
import type { ShowToast } from "../types";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface SubmittalsTabProps {
  T: Theme;
  divisions: { id: string }[];
  submittals: Submittal[];
  setSubmittals: React.Dispatch<React.SetStateAction<Submittal[]>>;
  submittalTemplates: SubmittalTemplateCustomLayer;
  setSubmittalTemplates: React.Dispatch<React.SetStateAction<SubmittalTemplateCustomLayer>>;
  subDraft: Partial<Submittal> | null;
  setSubDraft: React.Dispatch<React.SetStateAction<Partial<Submittal> | null>>;
  subEditId: string | null;
  setSubEditId: React.Dispatch<React.SetStateAction<string | null>>;
  subFilterStatus: string;
  setSubFilterStatus: React.Dispatch<React.SetStateAction<any>>;
  subFilterDiv: string;
  setSubFilterDiv: React.Dispatch<React.SetStateAction<string>>;
  /** Opens the Procore submittal export. Absent off a cloud project. */
  onExportProcore?: () => void;
  subTemplateEditorExpanded: boolean;
  setSubTemplateEditorExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  subTemplateExpandedDiv: string | null;
  setSubTemplateExpandedDiv: React.Dispatch<React.SetStateAction<string | null>>;
  setSubAutoPopDone: React.Dispatch<React.SetStateAction<boolean>>;
  submittalsSnapshot: () => void;
  showToast: ShowToast;
  subTemplateImportRef: React.RefObject<HTMLInputElement>;
}

export default function SubmittalsTab({
  T, divisions, submittals, setSubmittals, submittalTemplates, setSubmittalTemplates, onExportProcore,
  subDraft, setSubDraft, subEditId, setSubEditId,
  subFilterStatus, setSubFilterStatus, subFilterDiv, setSubFilterDiv,
  subTemplateEditorExpanded, setSubTemplateEditorExpanded,
  subTemplateExpandedDiv, setSubTemplateExpandedDiv,
  setSubAutoPopDone, submittalsSnapshot, showToast, subTemplateImportRef,
}: SubmittalsTabProps) {
  const enabledDivs = ALL_DIVISIONS.filter(d => divisions.some(ed => ed.id === d.id));

  /**
   * What the estimate is offering, and what the user has said yes to.
   *
   * Auto-populate used to put every template for every enabled division
   * straight on the log — several hundred rows on a full estimate, with no
   * warning and no way back but deleting them one at a time. Nothing is
   * ticked when this opens: what belongs on a submittal log is a judgement
   * about the job, so the default is to add nothing and the user says what
   * they want.
   */
  const [proposal, setProposal] = React.useState<ReturnType<typeof proposeSubmittals> | null>(null);
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  const allCandidates: SubmittalCandidate[] = React.useMemo(
    () => (proposal ? proposal.groups.flatMap(g => g.items) : []), [proposal]);
  const divName = (id: string) => {
    const d = ALL_DIVISIONS.find(x => x.id === id);
    return d ? `${d.code} · ${d.name}` : id;
  };
  const toggle = (key: string) => setPicked(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });
  const setMany = (keys: string[], on: boolean) => setPicked(prev => {
    const n = new Set(prev); keys.forEach(k => on ? n.add(k) : n.delete(k)); return n;
  });
  const commitPicked = () => {
    const chosen = allCandidates.filter(c => picked.has(c.key));
    if (!chosen.length) return;
    submittalsSnapshot();
    setSubmittals(buildSubmittals(submittals, chosen));
    setSubAutoPopDone(true);
    setProposal(null); setPicked(new Set());
    showToast(`Added ${chosen.length} submittal${chosen.length !== 1 ? "s" : ""}`);
  };
  const uncoveredDivs = enabledDivs.filter(div => {
    const b = DIVISION_SUBMITTAL_TEMPLATES[div.id] || [];
    const ds = new Set(submittalTemplates.disabled[div.id] || []);
    return b.filter(t => !ds.has(t.description)).length + (submittalTemplates.custom[div.id] || []).length === 0;
  });
  const filtered = submittals.filter(s =>
    (subFilterStatus === "all" || s.status === subFilterStatus) &&
    (subFilterDiv === "all" || s.divisionId === subFilterDiv)
  );
  const statusCounts = Object.fromEntries(
    (["not-started", "info-requested", "in-review", "approved", "rejected", "not-required"] as SubmittalStatus[])
      .map(st => [st, submittals.filter(s => s.status === st).length])
  );
  const divMap = Object.fromEntries(ALL_DIVISIONS.map(d => [d.id, d]));

  const saveSubDraft = () => {
    if (!subDraft?.description?.trim()) return;
    submittalsSnapshot();
    const now = new Date().toISOString().slice(0, 10);
    if (subEditId) {
      setSubmittals(prev => prev.map(s => s.id === subEditId ? { ...s, ...subDraft } as Submittal : s));
    } else {
      const newSub: Submittal = {
        id: "sub" + Date.now() + Math.random().toString(36).slice(2),
        divisionId: subDraft.divisionId || enabledDivs[0]?.id || "div3",
        specSection: subDraft.specSection || "", costCode: subDraft.costCode || "",
        description: subDraft.description || "", status: subDraft.status || "not-started",
        requiredByDate: subDraft.requiredByDate || "", responsibleParty: subDraft.responsibleParty || "",
        linkedItemId: subDraft.linkedItemId || "", notes: subDraft.notes || "",
        isBestPracticeFlag: subDraft.isBestPracticeFlag || false, createdAt: now,
      };
      setSubmittals(prev => [...prev, newSub]);
    }
    setSubDraft(null); setSubEditId(null);
  };

  const deleteSubmittal = (id: string) => { submittalsSnapshot(); setSubmittals(prev => prev.filter(s => s.id !== id)); };
  const statusOrder: SubmittalStatus[] = ["not-started", "info-requested", "in-review", "approved", "rejected", "not-required"];

  const exportSubCSV = () => {
    const rows = [["Division", "Spec Section", "Description", "Status", "Required By", "Responsible Party", "Notes", "Best Practice"]];
    filtered.forEach(s => {
      const d = divMap[s.divisionId];
      rows.push([d ? `${d.code} ${d.name}` : "", s.specSection, s.description,
        SUBMITTAL_STATUS_CONFIG[s.status].label, s.requiredByDate, s.responsibleParty, s.notes, s.isBestPracticeFlag ? "Yes" : ""]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "Submittal-Log.csv"; a.click();
  };

  const tbBtn: React.CSSProperties = {
    background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textSecondary,
    borderRadius: 3, padding: "5px 12px", fontSize: 11, fontFamily: FONT, cursor: "pointer", whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden", fontFamily: FONT }}>
      {/* LEFT NAV */}
      <div style={{ width: 180, background: T.bgPanel, borderRight: `1px solid ${T.borderPrimary}`, overflowY: "auto", flexShrink: 0, padding: "10px 0" }}>
        <div style={{ padding: "0 12px 8px", fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" }}>Divisions</div>
        <div onClick={() => setSubFilterDiv("all")} style={{ padding: "5px 12px", cursor: "pointer", fontSize: 12,
          background: subFilterDiv === "all" ? `${T.borderAccent}22` : "none",
          color: subFilterDiv === "all" ? T.borderAccent : T.textSecondary, borderRadius: 3, margin: "0 6px" }}>
          All Divisions
          <span style={{ float: "right", fontSize: 10, color: T.textWeak }}>{submittals.length}</span>
        </div>
        {ALL_DIVISIONS.filter(d => submittals.some(s => s.divisionId === d.id)).map(d => {
          const cnt = submittals.filter(s => s.divisionId === d.id).length;
          const isActive = subFilterDiv === d.id;
          return (
            <div key={d.id} onClick={() => setSubFilterDiv(isActive ? "all" : d.id)}
              style={{ padding: "7px 12px 7px 10px", cursor: "pointer", margin: "0 4px", borderRadius: 3,
                borderLeft: `2px solid ${isActive ? T.borderAccent : "transparent"}`,
                background: isActive ? T.bgAccentFaint : "none" }}>
              <div style={{ fontSize: 10, color: isActive ? T.borderAccent : T.textSubLabel, fontWeight: 700, marginBottom: 2, letterSpacing: ".1em" }}>{d.code}</div>
              <div style={{ fontSize: 11, lineHeight: 1.35, color: isActive ? T.borderAccent : T.textSecondary, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 4 }}>
                <span style={{ flex: 1 }}>{d.name}</span>
                <span style={{ fontSize: 10, color: T.textWeak, flexShrink: 0 }}>{cnt}</span>
              </div>
            </div>
          );
        })}
        <div style={{ borderTop: `1px solid ${T.borderSecondary}`, margin: "10px 8px", paddingTop: 8 }}>
          <div style={{ padding: "0 4px 6px", fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" }}>Status</div>
          {statusOrder.map(st => {
            const cfg = SUBMITTAL_STATUS_CONFIG[st];
            const cnt = submittals.filter(s => s.status === st).length;
            return (
              <div key={st} onClick={() => setSubFilterStatus(subFilterStatus === st ? "all" : st)}
                style={{ padding: "4px 8px", cursor: "pointer", fontSize: 11, borderRadius: 3, margin: "1px 0",
                  background: subFilterStatus === st ? cfg.bg : "none",
                  color: subFilterStatus === st ? cfg.color : T.textMuted }}>
                <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: cfg.color, marginRight: 5 }} />
                {cfg.label}
                <span style={{ float: "right", fontSize: 10 }}>{cnt}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* MAIN AREA */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* TOOLBAR */}
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.borderPrimary}`, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flexShrink: 0, background: T.bgApp }}>
          <button style={{ ...tbBtn, background: T.borderAccent, border: "none", color: T.textOnGold, fontWeight: 700 }}
            onClick={() => {
              const p = proposeSubmittals(enabledDivs, submittals, submittalTemplates);
              if (p.total === 0) { showToast("Nothing new to add — the log already covers every template"); return; }
              setProposal(p); setPicked(new Set());
            }}>
            <Icon.Sparkle size={13} /> Auto-Populate from Estimate
          </button>
          {uncoveredDivs.length > 0 && (
            <span title={`No templates for: ${uncoveredDivs.map(d => d.code).join(", ")}`}
              style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: "rgba(212,106,106,0.15)", border: "1px solid rgba(212,106,106,0.4)", color: "#d46a6a", cursor: "default", display: "inline-flex", alignItems: "center", gap: 4 }}>
              ⚠ {uncoveredDivs.length} div{uncoveredDivs.length !== 1 ? "s" : ""} uncovered
            </span>
          )}
          <button style={tbBtn} onClick={() => { setSubDraft({ status: "not-started", divisionId: enabledDivs[0]?.id || "div3" }); setSubEditId(null); }}>+ Add Submittal</button>
          <button style={tbBtn} onClick={exportSubCSV} disabled={filtered.length === 0}>↓ Export CSV</button>
          {/* Direct API by default; the modal also writes Procore's own
              import template for the rows their API refuses. */}
          {onExportProcore && submittals.length > 0 && (
            <button style={tbBtn} onClick={onExportProcore}
              title="Send these submittals to a Procore project">
              <Icon.Upload size={12} /> To Procore
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button style={{ ...tbBtn, background: subTemplateEditorExpanded ? T.bgAccentSubtle : undefined, border: `1px solid ${subTemplateEditorExpanded ? T.borderAccent : T.borderSecondary}`, color: subTemplateEditorExpanded ? T.textSubLabel : T.textWeak }}
            onClick={() => setSubTemplateEditorExpanded(v => !v)}>
            <Icon.Settings size={12} /> Templates{subTemplateEditorExpanded ? " ▲" : " ▼"}
          </button>
          <span style={{ fontSize: 11, color: T.textWeak }}>{filtered.length} of {submittals.length} item{submittals.length !== 1 ? "s" : ""}</span>
        </div>

        {/* TEMPLATE EDITOR PANEL */}
        {subTemplateEditorExpanded && (
          <div style={{ background: T.bgPanel, borderBottom: `1px solid ${T.borderPrimary}`, padding: "12px 16px", flexShrink: 0, maxHeight: 420, overflowY: "auto" as const }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, letterSpacing: "0.04em" }}>SUBMITTAL TEMPLATES</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={tbBtn} onClick={() => {
                  const data = JSON.stringify(submittalTemplates, null, 2);
                  const blob = new Blob([data], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = "submittal_templates.json"; a.click(); URL.revokeObjectURL(url);
                }}>↓ Export JSON</button>
                <button style={tbBtn} onClick={() => subTemplateImportRef.current?.click()}>↑ Import JSON</button>
                {(Object.keys(submittalTemplates.custom).length > 0 || Object.keys(submittalTemplates.disabled).length > 0) && (
                  <button style={{ ...tbBtn, color: "#d46a6a", borderColor: "rgba(212,106,106,0.4)" }}
                    onClick={() => { if (window.confirm("Reset all template customizations to defaults?")) setSubmittalTemplates({ custom: {}, disabled: {} }); }}>
                    ↺ Reset
                  </button>
                )}
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 10, lineHeight: 1.5 }}>
              Customize which items appear when you auto-populate submittals. Built-in items can be disabled; you can also add custom items per division.
            </div>
            {enabledDivs.length === 0 && <div style={{ color: T.textMuted, fontSize: 12 }}>No divisions enabled in the estimate.</div>}
            {enabledDivs.map(div => {
              const builtIns = DIVISION_SUBMITTAL_TEMPLATES[div.id] || [];
              const disabledSet = new Set(submittalTemplates.disabled[div.id] || []);
              const custom = submittalTemplates.custom[div.id] || [];
              const activeCount = builtIns.filter(t => !disabledSet.has(t.description)).length + custom.length;
              const isExpanded = subTemplateExpandedDiv === div.id;
              return (
                <div key={div.id} style={{ marginBottom: 4, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: T.bgApp, cursor: "pointer", userSelect: "none" as const }}
                    onClick={() => setSubTemplateExpandedDiv(isExpanded ? null : div.id)}>
                    <span style={{ fontSize: 11, color: T.textWeak, width: 16 }}>{isExpanded ? "▼" : "▶"}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, flex: 1 }}>{div.code} — {div.name}</span>
                    {activeCount === 0
                      ? <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "rgba(212,106,106,0.15)", border: "1px solid rgba(212,106,106,0.4)", color: "#d46a6a" }}>⚠ No active templates</span>
                      : <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: T.bgAccentFaint, border: `1px solid ${T.borderAccent}`, color: T.textSubLabel }}>{activeCount} template{activeCount !== 1 ? "s" : ""}</span>
                    }
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "8px 10px 10px 10px", background: T.bgPanel }}>
                      {builtIns.length > 0 && (<>
                        <div style={{ fontSize: 10, color: T.textWeak, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 5 }}>BUILT-IN</div>
                        {builtIns.map((t, i) => {
                          const isDisabled = disabledSet.has(t.description);
                          return (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 3, marginBottom: 2, background: isDisabled ? "rgba(212,106,106,0.06)" : T.bgAccentFaint, opacity: isDisabled ? 0.6 : 1 }}>
                              <input type="checkbox" checked={!isDisabled} style={{ accentColor: T.borderAccent, cursor: "pointer" }}
                                onChange={() => {
                                  setSubmittalTemplates(prev => {
                                    const cur = new Set(prev.disabled[div.id] || []);
                                    if (isDisabled) cur.delete(t.description); else cur.add(t.description);
                                    return { ...prev, disabled: { ...prev.disabled, [div.id]: [...cur] } };
                                  });
                                }} />
                              <span style={{ flex: 1, fontSize: 11, color: T.textSecondary }}>{t.description}</span>
                              <span style={{ fontSize: 10, color: T.textWeak, fontFamily: "monospace" }}>{t.specSection}</span>
                              {t.isBestPracticeFlag && <span title="Best Practice" style={{ fontSize: 10, color: T.textSubLabel }}>★</span>}
                            </div>
                          );
                        })}
                      </>)}
                      {custom.length > 0 && (<>
                        <div style={{ fontSize: 10, color: T.textWeak, fontWeight: 600, letterSpacing: "0.06em", marginTop: 8, marginBottom: 5 }}>CUSTOM</div>
                        {custom.map((t, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 3, marginBottom: 2, background: "rgba(100,200,120,0.06)" }}>
                            <span style={{ flex: 1, fontSize: 11, color: T.textSecondary }}>{t.description}</span>
                            <span style={{ fontSize: 10, color: T.textWeak, fontFamily: "monospace" }}>{t.specSection}</span>
                            <button style={{ background: "none", border: "none", color: "#d46a6a", cursor: "pointer", fontSize: 13, padding: "0 2px", lineHeight: 1 }}
                              onClick={() => {
                                setSubmittalTemplates(prev => {
                                  const cur = [...(prev.custom[div.id] || [])];
                                  cur.splice(i, 1);
                                  return { ...prev, custom: { ...prev.custom, [div.id]: cur } };
                                });
                              }}><Icon.X size={10} /></button>
                          </div>
                        ))}
                      </>)}
                      <AddCustomTemplateItemForm T={T} onAdd={item => {
                        setSubmittalTemplates(prev => ({ ...prev, custom: { ...prev.custom, [div.id]: [...(prev.custom[div.id] || []), item] } }));
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* STATUS SUMMARY BAR */}
        {submittals.length > 0 && (
          <div style={{ display: "flex", gap: 6, padding: "8px 16px", borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0, background: T.bgPanel, flexWrap: "wrap" }}>
            {statusOrder.map(st => {
              const cfg = SUBMITTAL_STATUS_CONFIG[st]; const cnt = statusCounts[st] || 0;
              return (
                <div key={st} onClick={() => setSubFilterStatus(subFilterStatus === st ? "all" : st)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 10, cursor: "pointer",
                    background: subFilterStatus === st ? cfg.bg : "rgba(255,255,255,0.04)", border: `1px solid ${subFilterStatus === st ? cfg.color : T.borderSecondary}`,
                    color: subFilterStatus === st ? cfg.color : T.textMuted, fontSize: 11 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color }} />
                  {cfg.label} · {cnt}
                </div>
              );
            })}
            {(subFilterStatus !== "all" || subFilterDiv !== "all") && (
              <button onClick={() => { setSubFilterStatus("all"); setSubFilterDiv("all"); }}
                style={{ ...tbBtn, border: "none", padding: "3px 8px", color: T.textWeak, fontSize: 11 }}>✕ Clear filters</button>
            )}
          </div>
        )}

        {/* TABLE */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 0 40px" }}>
          {filtered.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 300, gap: 16, color: T.textMuted }}>
              {submittals.length === 0 ? (
                <>
                  <div style={{ fontSize: 32 }}><Icon.Clipboard size={32} /></div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: T.textSecondary }}>No submittals yet</div>
                  <div style={{ fontSize: 12, textAlign: "center", maxWidth: 340, lineHeight: 1.6 }}>
                    Click <strong>Auto-Populate from Estimate</strong> to generate a submittal list from your active CSI divisions, or add items manually.
                  </div>
                </>
              ) : (
                <>
                  <div><Icon.Search size={24} /></div>
                  <div style={{ fontSize: 13 }}>No submittals match the current filters</div>
                  <button onClick={() => { setSubFilterStatus("all"); setSubFilterDiv("all"); }} style={tbBtn}>Clear filters</button>
                </>
              )}
            </div>
          )}
          {ALL_DIVISIONS.filter(d => filtered.some(s => s.divisionId === d.id)).map(div => {
            const divSubs = filtered.filter(s => s.divisionId === div.id);
            return (
              <div key={div.id}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 16px", background: `${T.borderAccent}10`,
                  borderBottom: `1px solid ${T.borderSecondary}`, borderTop: `2px solid ${T.borderAccent}30`,
                  position: "sticky", top: 0, zIndex: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.borderAccent, letterSpacing: ".05em" }}>{div.code} — {div.name}</span>
                  <span style={{ fontSize: 10, color: T.textWeak }}>{divSubs.length} item{divSubs.length !== 1 ? "s" : ""}</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <colgroup>
                    <col style={{ width: "7%", minWidth: 58 }} />
                    <col style={{ width: "38%" }} />
                    <col style={{ width: "14%", minWidth: 110 }} />
                    <col style={{ width: "13%", minWidth: 100 }} />
                    <col style={{ width: "22%", minWidth: 120 }} />
                    <col style={{ width: "6%", minWidth: 52 }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: T.bgPanel }}>
                      {["Spec §", "Description", "Status", "Required By", "Responsible Party"].map(h => (
                        <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontSize: 10, color: T.textWeak, letterSpacing: ".08em", fontWeight: 600, textTransform: "uppercase" }}>{h}</th>
                      ))}
                      <th style={{ padding: "5px 8px" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {divSubs.map((s, si) => {
                      const cfg = SUBMITTAL_STATUS_CONFIG[s.status];
                      return (
                        <tr key={s.id} style={{ background: si % 2 === 0 ? "transparent" : T.bgPanel, borderBottom: `1px solid ${T.borderPrimary}22` }}>
                          <td style={{ padding: "7px 12px", color: T.textWeak, fontSize: 10, whiteSpace: "nowrap" }}>{s.specSection}</td>
                          <td style={{ padding: "7px 8px", color: T.textPrimary }}>
                            <div style={{ lineHeight: 1.4 }}>
                              {s.description}{s.isBestPracticeFlag && <span title="Commonly required — don't forget!" style={{ marginLeft: 5, fontSize: 10, color: "#c9a84c", flexShrink: 0 }}>★</span>}
                            </div>
                            {s.notes && <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2, lineHeight: 1.4 }}>{s.notes}</div>}
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>
                            <select value={s.status}
                              onChange={e => setSubmittals(prev => prev.map(x => x.id === s.id ? { ...x, status: e.target.value as SubmittalStatus } : x))}
                              style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}55`,
                                borderRadius: 10, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: FONT,
                                whiteSpace: "nowrap", width: "100%", maxWidth: 136, appearance: "none", textAlign: "center" }}>
                              {(["not-started", "info-requested", "in-review", "approved", "rejected", "not-required"] as SubmittalStatus[]).map(st => (
                                <option key={st} value={st}>{SUBMITTAL_STATUS_CONFIG[st].label}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: "7px 8px" }}>
                            <input type="date" value={s.requiredByDate}
                              onChange={e => setSubmittals(prev => prev.map(x => x.id === s.id ? { ...x, requiredByDate: e.target.value } : x))}
                              style={{ background: "none", border: "none", color: s.requiredByDate ? T.textSecondary : T.textWeak, fontSize: 11, fontFamily: FONT, cursor: "pointer", width: "100%", minWidth: 0 }} />
                          </td>
                          <td style={{ padding: "7px 8px" }}>
                            <input value={s.responsibleParty} placeholder="—"
                              onChange={e => setSubmittals(prev => prev.map(x => x.id === s.id ? { ...x, responsibleParty: e.target.value } : x))}
                              style={{ background: "none", border: "none", color: T.textSecondary, fontSize: 11, fontFamily: FONT, width: "100%", outline: "none", minWidth: 0 }} />
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                            <button onClick={() => { setSubDraft({ ...s }); setSubEditId(s.id); }}
                              style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", fontSize: 12, padding: "2px 5px", fontFamily: FONT }} title="Edit"><Icon.Edit size={12} /></button>
                            <button onClick={() => deleteSubmittal(s.id)}
                              style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", fontSize: 12, padding: "2px 5px", fontFamily: FONT }} title="Delete"><Icon.X size={10} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      {/* ADD/EDIT MODAL */}
      {subDraft && (() => {
        const modalField = (label: string, child: React.ReactNode, mb = 14) => (
          <div style={{ marginBottom: mb }}>
            <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 4, letterSpacing: ".06em", textTransform: "uppercase" }}>{label}</div>
            {child}
          </div>
        );
        const selStyle: React.CSSProperties = { width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textPrimary, padding: "6px 10px", borderRadius: 3, fontSize: 12, fontFamily: FONT };
        const inStyle: React.CSSProperties = { width: "100%", boxSizing: "border-box", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textPrimary, padding: "6px 10px", borderRadius: 3, fontSize: 12, fontFamily: FONT, outline: "none" };
        const divTemplates = DIVISION_SUBMITTAL_TEMPLATES[subDraft.divisionId || ""] || [];
        return (
          <>
            <div onClick={() => { setSubDraft(null); setSubEditId(null); }} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.55)" }} />
            <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 901,
              background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6,
              width: 500, maxHeight: "85vh", overflowY: "auto", padding: "24px 28px", fontFamily: FONT }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.textPrimary, marginBottom: 18 }}>
                {subEditId ? "Edit Submittal" : "Add Submittal"}
              </div>
              {modalField("Division",
                <select value={subDraft.divisionId || ""} onChange={e => setSubDraft(prev => ({ ...prev, divisionId: e.target.value, specSection: "", costCode: "", description: "", isBestPracticeFlag: false }))} style={selStyle}>
                  <option value="">— Select division —</option>
                  {enabledDivs.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                </select>
              )}
              {divTemplates.length > 0 && modalField("Fill from standard submittal",
                <select defaultValue="" onChange={e => {
                  const idx = parseInt(e.target.value);
                  if (isNaN(idx)) return;
                  const t = divTemplates[idx];
                  setSubDraft(prev => ({ ...prev, specSection: t.specSection, costCode: t.costCode, description: t.description, isBestPracticeFlag: t.isBestPracticeFlag }));
                }} style={{ ...selStyle, borderColor: T.borderAccent, color: T.borderAccent }}>
                  <option value="">— Choose a common submittal —</option>
                  {divTemplates.map((t, i) => <option key={i} value={i}>{t.specSection} · {t.description}{t.isBestPracticeFlag ? " ★" : ""}</option>)}
                </select>
              , 12)}
              {modalField("Spec Section", <input type="text" value={subDraft.specSection || ""} placeholder="e.g. 03300" onChange={e => setSubDraft(prev => ({ ...prev, specSection: e.target.value }))} style={inStyle} />)}
              {modalField("Description", <input type="text" value={subDraft.description || ""} placeholder="e.g. Concrete Mix Design" onChange={e => setSubDraft(prev => ({ ...prev, description: e.target.value }))} style={inStyle} />)}
              {modalField("Status",
                <select value={subDraft.status || "not-started"} onChange={e => setSubDraft(prev => ({ ...prev, status: e.target.value as SubmittalStatus }))} style={selStyle}>
                  {(["not-started", "info-requested", "in-review", "approved", "rejected", "not-required"] as SubmittalStatus[]).map(s => (
                    <option key={s} value={s}>{SUBMITTAL_STATUS_CONFIG[s].label}</option>
                  ))}
                </select>
              )}
              {modalField("Required By", <input type="date" value={subDraft.requiredByDate || ""} onChange={e => setSubDraft(prev => ({ ...prev, requiredByDate: e.target.value }))} style={inStyle} />)}
              {modalField("Responsible Party", <input type="text" value={subDraft.responsibleParty || ""} placeholder="e.g. Summit MEP Group" onChange={e => setSubDraft(prev => ({ ...prev, responsibleParty: e.target.value }))} style={inStyle} />)}
              {modalField("Notes", <textarea value={subDraft.notes || ""} placeholder="Any additional notes..." onChange={e => setSubDraft(prev => ({ ...prev, notes: e.target.value }))} style={{ ...inStyle, resize: "vertical", minHeight: 70 }} />, 18)}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
                <input type="checkbox" id="sub-bp" checked={subDraft.isBestPracticeFlag || false} onChange={e => setSubDraft(prev => ({ ...prev, isBestPracticeFlag: e.target.checked }))} />
                <label htmlFor="sub-bp" style={{ fontSize: 12, color: T.textSecondary, cursor: "pointer" }}>Flag as commonly required / key submittal</label>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => { setSubDraft(null); setSubEditId(null); }} style={{ ...tbBtn }}>Cancel</button>
                <button onClick={saveSubDraft} disabled={!subDraft.description?.trim()}
                  style={{ background: T.borderAccent, border: "none", color: T.textOnGold, fontWeight: 700, padding: "7px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT, cursor: "pointer", opacity: subDraft.description?.trim() ? 1 : 0.5 }}>
                  {subEditId ? "Save Changes" : "Add Submittal"}
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── What the estimate is offering ──
          Nothing ticked when it opens. The old behaviour put every template
          for every enabled division on the log in one click; on a full
          estimate that is several hundred rows, and the only way back was
          deleting them one at a time. */}
      {proposal && (
        <div className="modal-backdrop" onMouseDown={() => setProposal(null)}>
          <div onMouseDown={e => e.stopPropagation()}
            style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6,
              width: "min(760px, calc(100vw - 48px))", maxHeight: "min(78vh, 760px)",
              display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: FONT }}>

            <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${T.borderPrimary}` }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>Add submittals from the estimate</div>
              <div style={{ fontSize: 11, color: T.textWeak, marginTop: 4, lineHeight: 1.5 }}>
                {proposal.total} suggestion{proposal.total !== 1 ? "s" : ""} across {proposal.groups.length} division{proposal.groups.length !== 1 ? "s" : ""},
                from your templates. Nothing is selected — tick what belongs on this job.
                {proposal.uncoveredDivIds.length > 0 && ` ${proposal.uncoveredDivIds.length} enabled division${proposal.uncoveredDivIds.length !== 1 ? "s have" : " has"} no template.`}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" as const }}>
                <button style={tbBtn} onClick={() => setMany(allCandidates.map(c => c.key), true)}>Select all</button>
                <button style={tbBtn} onClick={() => setPicked(new Set())} disabled={picked.size === 0}>Clear</button>
                <button style={tbBtn}
                  title="The items your templates flag as standard practice"
                  onClick={() => setMany(allCandidates.filter(c => c.isBestPracticeFlag).map(c => c.key), true)}>
                  {"★"} Best practice ({allCandidates.filter(c => c.isBestPracticeFlag).length})
                </button>
              </div>
            </div>

            <div style={{ overflowY: "auto", padding: "4px 20px 12px" }}>
              {proposal.groups.map(g => {
                const keys = g.items.map(i => i.key);
                const on = keys.filter(k => picked.has(k)).length;
                return (
                  <div key={g.divisionId} style={{ marginTop: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 5,
                      borderBottom: `1px solid ${T.borderPrimary}` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, flex: 1 }}>{divName(g.divisionId)}</span>
                      <span style={{ fontSize: 10, color: T.textWeak }}>{on} of {keys.length}</span>
                      <button style={{ ...tbBtn, padding: "2px 8px", fontSize: 10 }}
                        onClick={() => setMany(keys, on < keys.length)}>
                        {on < keys.length ? "All" : "None"}
                      </button>
                    </div>
                    {g.items.map(it => {
                      const checked = picked.has(it.key);
                      return (
                        <label key={it.key}
                          style={{ display: "flex", alignItems: "center", gap: 9, padding: "5px 2px",
                            borderBottom: `1px solid ${T.borderPrimary}40`, cursor: "pointer" }}>
                          <input type="checkbox" checked={checked} onChange={() => toggle(it.key)}
                            style={{ accentColor: T.borderAccent, flexShrink: 0 }} />
                          {it.specSection && (
                            <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textWeak, width: 58, flexShrink: 0 }}>{it.specSection}</span>
                          )}
                          <span style={{ flex: 1, minWidth: 0, fontSize: 11,
                            color: checked ? T.textPrimary : T.textMuted }}>{it.description}</span>
                          {it.isBestPracticeFlag && (
                            <span title="Standard practice" style={{ fontSize: 10, color: T.borderAccent, flexShrink: 0 }}>{"★"}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderPrimary}`,
              display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: T.textWeak, flex: 1 }}>
                {picked.size === 0 ? "Nothing selected" : `${picked.size} of ${proposal.total} selected`}
              </span>
              <button style={tbBtn} onClick={() => setProposal(null)}>Cancel</button>
              <button disabled={picked.size === 0} onClick={commitPicked}
                style={{ ...tbBtn, background: picked.size ? T.borderAccent : "transparent",
                  border: picked.size ? "none" : `1px solid ${T.borderSecondary}`,
                  color: picked.size ? T.textOnGold : T.textWeak,
                  fontWeight: 700, cursor: picked.size ? "pointer" : "default" }}>
                Add {picked.size > 0 ? `${picked.size} ` : ""}submittal{picked.size === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
